import { useState, useEffect } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import {
  Gauge, CalendarBlank, Broadcast, Bus, Handshake, ChartBar, GearSix,
  SignOut, List, X, MagnifyingGlass, CaretLeft, CaretRight,
  MapTrifold, Lightning, Ticket, CreditCard, Wrench, Warning, Users, IdentificationBadge, UserList, MapPin, Buildings,
  FileText, Path, Globe, Database, Megaphone,
} from "@phosphor-icons/react";
import { clearAuth, getUser } from "@/lib/auth";
import { ITMS } from "@/constants/testIds";
import { cn } from "@/lib/utils";
import GlobalSearch from "@/components/GlobalSearch";
import NotificationBell from "@/components/NotificationBell";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: Gauge, testid: ITMS.navDashboard },
  { to: "/live-map", label: "Live Map & Tracking", icon: MapTrifold, testid: "nav-live-map" },
  { to: "/network-map", label: "Network Map", icon: Path, testid: "nav-network-map" },
  { to: "/schedule", label: "Schedule Designer", icon: CalendarBlank, testid: ITMS.navSchedule },
  { to: "/schedules", label: "Master Schedules", icon: CalendarBlank, testid: "nav-master-schedules" },
  { to: "/dispatch", label: "Dispatch Board", icon: Broadcast, testid: ITMS.navDispatch },
  { to: "/fleet", label: "Fleet Inventory", icon: Bus, testid: ITMS.navFleet },
  { to: "/operators", label: "Private Operators", icon: Handshake, testid: ITMS.navOperators },
  { to: "/ev", label: "EV Management", icon: Lightning, testid: "nav-ev" },
  { to: "/tickets", label: "Tickets & Revenue", icon: Ticket, testid: "nav-tickets" },
  { to: "/qr-tickets", label: "QR Bus Booking", icon: Ticket, testid: "nav-qr-tickets" },
  { to: "/transit-cards", label: "Smart Transit Card", icon: CreditCard, testid: "nav-transit-cards" },
  { to: "/passes", label: "Pass Management", icon: IdentificationBadge, testid: "nav-passes" },
  { to: "/applications", label: "Applications", icon: FileText, testid: "nav-applications" },
  { to: "/crew", label: "Crew Management", icon: UserList, testid: "nav-crew" },
  { to: "/bus-stops", label: "Bus Stops", icon: MapPin, testid: "nav-bus-stops" },
  { to: "/depots", label: "Depots", icon: Buildings, testid: "nav-depots" },
  { to: "/route-designer", label: "Route Designer", icon: MapPin, testid: "nav-route-designer" },
  { to: "/maintenance", label: "Maintenance", icon: Wrench, testid: "nav-maintenance" },
  { to: "/alerts", label: "Alerts & Incidents", icon: Warning, testid: "nav-alerts" },
  { to: "/service-alerts", label: "Service Alerts (Public)", icon: Megaphone, testid: "nav-service-alerts" },
  { to: "/reports", label: "Reports & Analytics", icon: ChartBar, testid: ITMS.navReports },
  { to: "/open-data", label: "Open Data Feeds", icon: Globe, testid: "nav-open-data" },
  { to: "/data-manager", label: "Data Manager (Bulk Upload)", icon: Database, testid: "nav-data-manager" },
  { to: "/admin", label: "Admin Settings", icon: GearSix, testid: ITMS.navAdmin },
  { to: "/users", label: "User Management", icon: Users, testid: "nav-users" },
];

export default function AppLayout() {
  // Desktop ≥ lg (1024) — sidebar docked & collapsible
  // < lg — sidebar hidden by default, drawer-overlay on demand
  const [collapsed, setCollapsed] = useState(false);    // desktop dock width
  const [drawerOpen, setDrawerOpen] = useState(false);   // mobile drawer state
  const navigate = useNavigate();
  const location = useLocation();
  const user = getUser();

  // Auto-close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const logout = () => { clearAuth(); navigate("/login"); };

  const sidebarWidth = collapsed ? "lg:w-[68px]" : "lg:w-[248px]";

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-slate-50 text-slate-900">
      {/* Mobile backdrop */}
      {drawerOpen && (
        <div
          data-testid="sidebar-backdrop"
          onClick={() => setDrawerOpen(false)}
          className="fixed inset-0 bg-black/50 z-[1100] lg:hidden"
        />
      )}

      {/* Sidebar — drawer on <lg, docked on ≥lg */}
      <aside
        data-testid={ITMS.sidebar}
        className={cn(
          "flex flex-col bg-[#0F1A3D] text-slate-100 border-r border-slate-800/40 transition-[width,transform] duration-200 lg:z-50 max-lg:z-[1110]",
          // Desktop: docked, relative, width depends on collapsed
          "lg:relative lg:translate-x-0 lg:h-auto",
          sidebarWidth,
          // Mobile (<lg): fixed drawer, full height, slides in
          "max-lg:fixed max-lg:top-0 max-lg:left-0 max-lg:h-full max-lg:w-[260px]",
          drawerOpen ? "translate-x-0" : "max-lg:-translate-x-full"
        )}
      >
        <div className="flex items-center gap-3 h-14 px-4 border-b border-white/10 shrink-0">
          <div className="w-9 h-9 rounded-md bg-white text-[#1E3A8A] flex items-center justify-center font-display font-black">
            K
          </div>
          {!collapsed && (
            <div className="leading-tight flex-1 min-w-0">
              <div className="font-display font-bold text-sm truncate">KTCL - MajheApp</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400 truncate">Admin Control Center</div>
            </div>
          )}
          {/* Mobile close */}
          <button
            data-testid="sidebar-close-mobile"
            onClick={() => setDrawerOpen(false)}
            className="lg:hidden text-slate-300 hover:text-white"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto scroll-thin py-3">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                cn(
                  "group flex items-center gap-3 mx-2 my-0.5 px-3 py-2.5 rounded-md text-sm transition-colors",
                  isActive
                    ? "bg-white/10 text-white border-l-2 border-l-[#22C55E]"
                    : "text-slate-300 hover:bg-white/5 hover:text-white"
                )
              }
            >
              <n.icon size={20} weight="duotone" />
              <span className={cn("truncate", collapsed && "lg:hidden")}>{n.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Desktop collapse only — hidden on mobile drawer */}
        <button
          data-testid={ITMS.sidebarToggle}
          onClick={() => setCollapsed((c) => !c)}
          className="hidden lg:flex items-center gap-2 m-3 px-3 py-2 rounded-md text-xs text-slate-300 hover:bg-white/5 hover:text-white transition-colors border border-white/10"
        >
          {collapsed ? <CaretRight size={16} /> : <CaretLeft size={16} />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Topbar */}
        <header className="h-14 bg-white border-b border-slate-200 flex items-center px-3 sm:px-4 lg:px-6 gap-2 sm:gap-3 lg:gap-4 shrink-0">
          {/* Hamburger only on <lg */}
          <button
            data-testid="topbar-hamburger"
            className="lg:hidden text-slate-600 hover:text-slate-900 -ml-1 p-1"
            onClick={() => setDrawerOpen(true)}
            aria-label="Open menu"
          >
            <List size={22} />
          </button>
          <div className="flex-1 min-w-0">
            <GlobalSearch />
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2 lg:gap-3 shrink-0">
            <span className="hidden xl:flex items-center gap-2 text-xs">
              <span className="status-dot status-Available"></span>
              <span className="text-slate-600">Live · GPS Sync OK</span>
            </span>
            <NotificationBell />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-[#1E3A8A] text-white text-xs flex items-center justify-center font-semibold shrink-0">
                {user?.name?.[0] || "K"}
              </div>
              <div className="hidden lg:block leading-tight">
                <div className="text-xs font-semibold">{user?.name || "Admin"}</div>
                <div className="text-[10px] text-slate-500">{user?.role || "Super Admin"}</div>
              </div>
            </div>
            <button
              data-testid={ITMS.logout}
              onClick={logout}
              className="text-slate-500 hover:text-[#EF4444] transition-colors p-1"
              title="Logout"
            >
              <SignOut size={18} />
            </button>
          </div>
        </header>

        {/* Content */}
        <main className="flex-1 overflow-auto scroll-thin">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
